var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Divider, Tooltip, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WatchIcon from '@mui/icons-material/Watch';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import BedIcon from '@mui/icons-material/Bed';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import CircleIcon from '@mui/icons-material/Circle';
import moment from 'moment';
var getStatusColor = function (status) {
    var normalizedStatus = status === null || status === void 0 ? void 0 : status.toLowerCase().trim();
    if (!normalizedStatus ||
        ['-', 'na', 'not available', 'not connected', 'not-available'].includes(normalizedStatus)) {
        return '#7b8493';
    }
    if (['online', 'high', 'good', 'excellent', 'strong'].includes(normalizedStatus)) {
        return '#16a34a';
    }
    if (['offline', 'low', 'poor', 'weak', 'critical'].includes(normalizedStatus)) {
        return '#dc2626';
    }
    return '#d79500';
};
var getStatusLabel = function (status) {
    if (!status || status === '-' || status.toLowerCase() === 'na')
        return 'Not Connected';
    return status.charAt(0).toUpperCase() + status.slice(1);
};
var getFormattedDate = function (date, dateOnly) {
    if (dateOnly === void 0) { dateOnly = false; }
    if (!date)
        return 'Not Available';
    // Fix timezone formats:
    // +0000Z  → +00:00
    // +00:00Z → +00:00
    var cleaned = date
        .replace(/(\+\d{4})Z$/, function (_, tz) { return "+".concat(tz.slice(1, 3), ":").concat(tz.slice(3, 5)); })
        .replace(/(\+\d{2}:\d{2})Z$/, '$1');
    var parsed = moment.utc(cleaned);
    return parsed.isValid()
        ? parsed.format(dateOnly ? 'MM/DD/YYYY' : 'MM/DD/YYYY HH:mm:ss') // UTC output
        : 'Not Available';
};
var getMetric = function (device, metric) {
    var _a, _b, _c, _d, _e, _f, _g;
    var metricData = device === null || device === void 0 ? void 0 : device[metric];
    var explicitStatus = (typeof metricData === 'object' ? metricData === null || metricData === void 0 ? void 0 : metricData.status : metricData) ||
        (device === null || device === void 0 ? void 0 : device["".concat(metric, "_status")]) ||
        (device === null || device === void 0 ? void 0 : device["".concat(metric, "Status")]) ||
        undefined;
    var value = (_c = (_b = (_a = (typeof metricData === 'object' ? metricData === null || metricData === void 0 ? void 0 : metricData.value : undefined)) !== null && _a !== void 0 ? _a : device === null || device === void 0 ? void 0 : device["".concat(metric, "_level")]) !== null && _b !== void 0 ? _b : device === null || device === void 0 ? void 0 : device["".concat(metric, "Level")]) !== null && _c !== void 0 ? _c : device === null || device === void 0 ? void 0 : device["".concat(metric, "_value")];
    var sourceUnit = (typeof metricData === 'object' ? metricData === null || metricData === void 0 ? void 0 : metricData.unit : undefined) ||
        (device === null || device === void 0 ? void 0 : device["".concat(metric, "_unit")]);
    var percentage = metric === 'battery'
        ? (_d = metricData === null || metricData === void 0 ? void 0 : metricData.battery_percentage) !== null && _d !== void 0 ? _d : device === null || device === void 0 ? void 0 : device.battery_percentage
        : (_e = device === null || device === void 0 ? void 0 : device.network_signal) !== null && _e !== void 0 ? _e : value;
    var voltage = metric === 'battery'
        ? (_g = (_f = metricData === null || metricData === void 0 ? void 0 : metricData.battery_voltage) !== null && _f !== void 0 ? _f : device === null || device === void 0 ? void 0 : device.battery_voltage) !== null && _g !== void 0 ? _g : (sourceUnit && ['v', 'mv'].includes(String(sourceUnit).toLowerCase())
            ? Number(value) * (String(sourceUnit).toLowerCase() === 'v' ? 1000 : 1)
            : undefined)
        : undefined;
    var signalStrength = Number(percentage);
    var signalStatus = percentage == null || percentage === ''
        ? undefined
        : Number.isFinite(signalStrength)
            ? Math.abs(signalStrength) >= 61 && Math.abs(signalStrength) <= 104
                ? 'High'
                : Math.abs(signalStrength) >= 105 && Math.abs(signalStrength) <= 120
                    ? 'Medium'
                    : Math.abs(signalStrength) >= 121 && Math.abs(signalStrength) <= 140
                        ? 'Low'
                        : undefined
            : undefined;
    var status = metric === 'signal'
        ? signalStatus || explicitStatus || 'Not Available'
        : explicitStatus ||
            (percentage == null && voltage == null
                ? 'Not Available'
                : Number(percentage !== null && percentage !== void 0 ? percentage : voltage) <= 20 || Number(voltage) <= 3600
                    ? 'Low'
                    : Number(percentage !== null && percentage !== void 0 ? percentage : voltage) <= 60 || Number(voltage) < 3900
                        ? 'Medium'
                        : 'High');
    var displayValue = metric === 'battery'
        ? percentage != null
            ? "".concat(percentage, "%").concat(voltage != null ? " (".concat(voltage, " mV)") : '')
            : voltage != null
                ? "".concat((Number(voltage) / 1000).toFixed(2), " V (").concat(voltage, " mV)")
                : undefined
        : percentage == null
            ? undefined
            : "-".concat(percentage, " dBm");
    return { status: String(status), value: displayValue };
};
var DeviceMetricStatus = function (_a) {
    var _b;
    var icon = _a.icon, label = _a.label, metric = _a.metric;
    var color = getStatusColor(metric.status);
    var statusLabel = getStatusLabel(metric.status);
    var normalizedStatus = (_b = metric.status) === null || _b === void 0 ? void 0 : _b.toLowerCase().trim();
    var isNotAvailable = ['not available', 'not-available', 'na', '-'].includes(normalizedStatus || '');
    var backgroundColor = isNotAvailable
        ? '#f8fafc'
        : color === '#16a34a'
            ? '#effaf5'
            : color === '#dc2626'
                ? '#fff1f2'
                : color === '#d79500'
                    ? '#fffbeb'
                    : '#f1f5f9';
    var borderColor = isNotAvailable
        ? '#dce2ea'
        : color === '#16a34a'
            ? '#c8efdc'
            : color === '#dc2626'
                ? '#fecdd3'
                : color === '#d79500'
                    ? '#fde68a'
                    : '#e2e8f0';
    ;
    return (_jsxs(Box, { sx: {
            flex: '1 1 0',
            minWidth: 0,
            width: 0,
            minHeight: 40,
            px: 0.75,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            border: "1px solid ".concat(borderColor),
            borderRadius: 1,
            backgroundColor: backgroundColor,
            overflow: 'hidden',
            boxSizing: 'border-box',
        }, children: [_jsx(Box, { "aria-hidden": 'true', sx: {
                    flex: '0 0 16px',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: color,
                    '& .MuiSvgIcon-root': {
                        width: 16,
                        height: 16,
                        color: 'inherit',
                    },
                }, children: icon }), _jsxs(Box, { sx: {
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    lineHeight: 1.2,
                }, children: [_jsxs(Typography, { sx: {
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#273242',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: [label, " -", ' ', _jsx(Box, { component: 'span', sx: {
                                    color: color,
                                }, children: statusLabel })] }), metric.value && (_jsx(Typography, { sx: {
                            mt: 0.25,
                            fontSize: 9,
                            color: '#697382',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: metric.value }))] })] }));
};
var DeviceStatusDetailsDialog = function (_a) {
    var open = _a.open, onClose = _a.onClose, deviceStatus = _a.deviceStatus, _b = _a.deviceMetrics, deviceMetrics = _b === void 0 ? {} : _b;
    var devices = [
        { key: 'watch', label: 'Watch', icon: _jsx(WatchIcon, { sx: { fontSize: 20 } }) },
        { key: 'scale', label: 'Scale', icon: _jsx(MonitorWeightIcon, { sx: { fontSize: 20 } }) },
        { key: 'sleep_mat', label: 'Sleep Mat', icon: _jsx(BedIcon, { sx: { fontSize: 20 } }) },
    ];
    return (_jsxs(Dialog, { open: open, onClose: function (event, reason) {
            if ('stopPropagation' in event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            onClose();
        }, onClick: function (e) { return e.stopPropagation(); }, maxWidth: "xs", fullWidth: true, PaperProps: {
            sx: {
                width: 420,
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: 2,
                p: 0,
            },
        }, children: [_jsxs(DialogTitle, { sx: {
                    fontWeight: 700,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '8px 10px 6px',
                    color: '#f8fafc',
                    backgroundColor: '#32602F',
                }, children: ["Device Status Details", _jsx(IconButton, { "aria-label": "close", onClick: function (e) {
                            e.stopPropagation();
                            onClose();
                        }, sx: {
                            color: '#64748b',
                            '&:hover': { color: '#1e293b' },
                        }, children: _jsx(CloseIcon, { fontSize: 'small', sx: { color: '#f8fafc' } }) })] }), _jsx(Divider, {}), _jsx(DialogContent, { sx: { p: '4px 10px 10px', boxSizing: 'border-box', overflowX: 'hidden' }, children: devices.map(function (_a) {
                    var key = _a.key, label = _a.label, icon = _a.icon;
                    var deviceStatusData = deviceStatus[key];
                    var device = __assign(__assign({}, (typeof deviceStatusData === 'object' ? deviceStatusData : {})), (deviceMetrics[key] || {}));
                    if (!deviceStatusData && Object.keys(device).length === 0)
                        return null;
                    var status = (device === null || device === void 0 ? void 0 : device.status) ||
                        (typeof deviceStatusData === 'string' ? deviceStatusData : '-');
                    var color = getStatusColor(status);
                    var statusLabel = getStatusLabel(status);
                    var battery = getMetric(device, 'battery');
                    var signal = getMetric(device, 'signal');
                    var lastSync = deviceStatus["".concat(key, "_last_sync")];
                    var formattedLastSync = getFormattedDate(lastSync, true);
                    return (_jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            py: 1,
                            borderBottom: '1px solid #f1f5f9',
                            '&:last-child': {
                                borderBottom: 'none',
                            },
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsx(Box, { sx: {
                                                    display: 'flex',
                                                    color: '#111827',
                                                    '& svg': { fontSize: 16 },
                                                }, children: icon }), _jsx(Typography, { sx: { fontWeight: 700, fontSize: 11, color: '#1e293b' }, children: label })] }), _jsx(Tooltip, { title: "Status: ".concat(statusLabel), children: _jsxs(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 0.5,
                                            }, children: [_jsx(Typography, { sx: {
                                                        fontSize: 10,
                                                        color: '#334155',
                                                        fontWeight: 600,
                                                    }, children: statusLabel }), _jsx(CircleIcon, { sx: { fontSize: 10, color: color } })] }) })] }), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    gap: 0.75,
                                    width: key === 'watch' ? 'calc(100% - 24px)' : 'calc(50% - 10px)',
                                    minWidth: 0,
                                    mt: 0.5,
                                }, children: [_jsx(DeviceMetricStatus, { icon: _jsx(BatteryFullIcon, { sx: { fontSize: 18 } }), label: 'Battery', metric: battery }), key === 'watch' && (_jsx(DeviceMetricStatus, { icon: _jsx(SignalCellularAltIcon, { sx: { fontSize: 18 } }), label: 'Signal', metric: signal }))] }), _jsxs(Box, { sx: { textAlign: 'left', width: '100%', pl: 3, mt: 0.4 }, children: [_jsxs(Typography, { sx: { fontSize: 9, color: '#64748b', mb: 0.2 }, children: [_jsx("strong", { children: "Last Measurement Taken:" }), ' ', getFormattedDate(device === null || device === void 0 ? void 0 : device.last_ingestion_date)] }), _jsxs(Typography, { sx: { fontSize: 9, color: '#64748b' }, children: [_jsx("strong", { children: "Last Sync up:" }), ' ', getFormattedDate(device === null || device === void 0 ? void 0 : device.last_session_date)] })] })] }, key));
                }) })] }));
};
export default DeviceStatusDetailsDialog;
